import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ImageSlotManager, slotsFromExistingImages, buildSlotPayload } from '../../components/ImageSlotManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
};

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '', category: 'residential', description: '', location: '',
    year: new Date().getFullYear().toString(),
  });
  const [slots, setSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title,
        category: project.category,
        description: project.description,
        location: project.location,
        year: project.year,
      });
      const existing = project.images && project.images.length > 0
        ? project.images
        : (project.image ? [project.image] : []);
      setSlots(slotsFromExistingImages(existing, project.imageOverlays || []));
    } else {
      setEditingProject(null);
      setFormData({
        title: '', category: 'residential', description: '', location: '',
        year: new Date().getFullYear().toString(),
      });
      setSlots([]);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
    setSlots([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUploadProgress(0);
    try {
      const token = localStorage.getItem('admin_token');
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('category', formData.category);
      fd.append('description', formData.description);
      fd.append('location', formData.location);
      fd.append('year', formData.year);
      const { hasFiles } = buildSlotPayload(slots, fd);

      const config = {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: hasFiles ? (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        } : undefined,
      };

      if (editingProject) {
        await axios.put(`${BACKEND_URL}/api/admin/projects/${editingProject.id}`, fd, config);
        toast.success('Project updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/projects`, fd, config);
        toast.success('Project created successfully!');
      }
      handleCloseModal();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const performDelete = async (projectId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project moved to Trash');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    } finally {
      setPendingDeleteId(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-gray-600">Loading projects...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Projects Management</h1>
          <Button
            onClick={() => handleOpenModal()}
            className="text-white font-semibold"
            style={{ backgroundColor: '#00CED1' }}
            data-testid="admin-projects-add-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Project
          </Button>
        </div>

        {projects.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">No projects yet. Click "Add New Project" to create one.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="overflow-hidden" data-testid={`admin-projects-card-${project.id}`}>
                <div className="h-48 bg-gray-100 relative">
                  {project.image ? (
                    <img
                      src={resolveUrl(project.image)}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-12 w-12 text-gray-300" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <span 
                      className="px-2 py-1 text-xs rounded text-white capitalize"
                      style={{ backgroundColor: project.category === 'commercial' ? '#FF0000' : '#00CED1' }}
                    >
                      {project.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg mb-2">{project.title}</h3>
                  <p className="text-sm text-gray-700 mb-3 line-clamp-2">{project.description}</p>
                  <div className="text-xs text-gray-600 mb-4">
                    <p>📍 {project.location}</p>
                    <p>📅 {project.year}</p>
                    <p className="mt-1">{(project.images || []).length || (project.image ? 1 : 0)} image(s)</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenModal(project)}
                      className="flex-1" data-testid={`admin-projects-edit-${project.id}`}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPendingDeleteId(project.id)}
                      className="flex-1 text-red-600 hover:text-red-700" data-testid={`admin-projects-delete-${project.id}`}>
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Project Title *</Label>
                <Input id="title" value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea id="description" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3} required />
              </div>
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input id="location" value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Sandton, Johannesburg" required />
              </div>
              <div>
                <Label htmlFor="year">Year *</Label>
                <Input id="year" value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="e.g., 2024" required />
              </div>

              <ImageSlotManager
                slots={slots}
                onSlotsChange={setSlots}
                testIdPrefix="admin-projects"
                isUploading={submitting}
                uploadProgress={uploadProgress}
              />

              <div className="flex space-x-2 pt-4">
                <Button type="submit" disabled={submitting} className="flex-1 text-white font-semibold"
                  style={{ backgroundColor: '#00CED1' }} data-testid="admin-projects-submit">
                  {submitting
                    ? (uploadProgress > 0 && uploadProgress < 100 ? `Uploading ${uploadProgress}%…` : 'Saving...')
                    : (editingProject ? 'Update Project' : 'Create Project')}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal}
                  className="flex-1" data-testid="admin-projects-cancel">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={!!pendingDeleteId}
          title="Move this project to Trash?"
          description="The project will be moved to Trash. You can restore it from there."
          confirmLabel="Delete"
          destructive
          onCancel={() => setPendingDeleteId(null)}
          onConfirm={() => performDelete(pendingDeleteId)}
          testId="admin-projects-confirm-delete"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminProjects;
